<template>
  <div class="device-block" role="dialog" aria-modal="true" aria-labelledby="device-block-title">
    <section class="device-block__card">
      <div class="device-block__icon"><i class="fa-solid fa-lock"></i></div>
      <p class="device-block__eyebrow">Proteccion operativa</p>
      <h1 id="device-block-title">Dispositivo temporalmente bloqueado</h1>
      <p class="device-block__lead">
        No existe ahora mismo una fuente de verdad compartida segura para pedidos, mesas y cobros.
      </p>

      <div class="device-block__reason" role="status">{{ reasonMessage }}</div>

      <dl>
        <div><dt>Este dispositivo</dt><dd>{{ currentName }} · {{ currentRole }}</dd></div>
        <div><dt>Principal</dt><dd>{{ primaryName }}</dd></div>
        <div><dt>Estado</dt><dd>{{ primaryStatusLabel }}</dd></div>
        <div><dt>Ultima comprobacion</dt><dd>{{ checkedAtLabel }}</dd></div>
      </dl>

      <p v-if="state.blockingError?.detail" class="device-block__error">{{ state.blockingError.detail }}</p>

      <div class="device-block__actions">
        <button class="device-block__primary" :disabled="checking" @click="checkAgain">
          <i class="fa-solid fa-rotate"></i>
          {{ checking ? 'Comprobando...' : 'Comprobar de nuevo' }}
        </button>
        <button v-if="state.userIsPartner" @click="manageDevices">Gestionar dispositivos</button>
        <button @click="logout">Cerrar sesion</button>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

const store = useStore();
const router = useRouter();
const checking = ref(false);
const state = computed(() => store.state.device);

const currentName = computed(() => state.value.device?.name || 'Navegador no registrado');
const currentRole = computed(() => (
  state.value.device?.is_primary ? 'Principal' : 'Secundario'
));
const primaryName = computed(() => state.value.primaryDevice?.name || 'Sin principal configurado');
const primaryStatusLabel = computed(() => ({
  ONLINE_SYNCED: 'Conectado y sincronizado',
  ONLINE_PENDING: 'Con cambios pendientes',
  SYNCING: 'Sincronizando',
  CONFLICT: 'Con conflictos',
  OFFLINE: 'Sin conexion',
  DISABLED: 'Deshabilitado',
  NO_PRIMARY: 'Sin principal',
  UNKNOWN: 'No verificable',
}[state.value.primaryEffectiveStatus || state.value.serverEffectiveState] || 'No verificable'));

const reasonMessage = computed(() => ({
  PRIMARY_OFFLINE: 'El dispositivo principal no esta conectado. Continua trabajando desde el terminal principal.',
  PRIMARY_PENDING: 'El dispositivo principal tiene cambios pendientes de sincronizacion. Este dispositivo se habilitara cuando termine.',
  PRIMARY_SYNCING: 'El dispositivo principal esta sincronizando la informacion del restaurante.',
  PRIMARY_CONFLICT: 'El dispositivo principal tiene conflictos pendientes de resolver.',
  PRIMARY_DISABLED: 'El dispositivo principal esta deshabilitado.',
  PRIMARY_UNKNOWN: 'No se puede verificar el estado del dispositivo principal.',
  NO_PRIMARY: 'El restaurante no tiene un dispositivo principal configurado.',
  SECONDARY_API_UNAVAILABLE: 'Este dispositivo no puede conectarse a EWU y no esta autorizado para trabajar sin conexion.',
  HYBRID_CLOUD_UNAVAILABLE: 'No se puede conectar con EWU y el servidor local todavía no está disponible para asumir la operativa.',
  RESTAURANT_LOCAL_MODE_ACTIVE: 'El restaurante ha activado la operativa local. Espera a que este terminal reciba la nueva autoridad.',
  RESTAURANT_RECOVERING_TO_CLOUD: 'El restaurante está sincronizando la vuelta a EWU cloud.',
  RESTAURANT_OPERATION_MODE_CONFLICT: 'El modo operativo necesita confirmación antes de aceptar nuevas operaciones.',
  DEVICE_NOT_REGISTERED: 'Este navegador todavia no esta registrado como dispositivo del restaurante.',
  DEVICE_REVOKED: 'Este dispositivo ha sido revocado y no puede realizar operaciones.',
  DEVICE_CREDENTIAL_INVALID: 'La credencial de este dispositivo no es valida. Un Partner debe renovarla.',
}[state.value.operationalAccessReason] || 'No se ha podido confirmar que el terminal principal este disponible.'));

const checkedAtLabel = computed(() => {
  const value = state.value.operationalAccessCheckedAt;
  return value ? new Date(value).toLocaleString('es-ES') : 'Pendiente';
});

async function checkAgain() {
  checking.value = true;
  try { await store.dispatch('device/refreshOperationalAccess'); } catch { /* Store keeps the reason. */ }
  finally { checking.value = false; }
}

function manageDevices() {
  router.push({ name: 'SincronizacionLocal' });
}

function logout() {
  store.commit('logout');
  router.push({ name: 'Login' });
}
</script>

<style scoped>
.device-block { position: fixed; inset: 0; z-index: 19000; display: grid; place-items: center; padding: 20px; background: rgba(15, 23, 42, .72); backdrop-filter: blur(8px); }
.device-block__card { width: min(620px, 100%); max-height: calc(100vh - 40px); overflow: auto; padding: 30px; border-radius: 24px; background: #fff; box-shadow: 0 24px 80px rgba(15, 23, 42, .35); color: #172033; }
.device-block__icon { width: 54px; height: 54px; display: grid; place-items: center; border-radius: 16px; background: #fff1e8; color: #e8651a; font-size: 22px; }
.device-block__eyebrow { margin: 20px 0 5px; color: #e8651a; font-size: 12px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
h1 { margin: 0; font-size: clamp(25px, 5vw, 36px); line-height: 1.1; }
.device-block__lead { margin: 14px 0 18px; color: #526078; line-height: 1.55; }
.device-block__reason { padding: 14px 16px; border-left: 4px solid #e8651a; border-radius: 10px; background: #fff7ed; font-weight: 700; line-height: 1.45; }
dl { display: grid; gap: 1px; margin: 20px 0; overflow: hidden; border: 1px solid #e5e7eb; border-radius: 14px; background: #e5e7eb; }
dl div { display: flex; justify-content: space-between; gap: 20px; padding: 11px 14px; background: #fff; }
dt { color: #64748b; font-weight: 600; } dd { margin: 0; text-align: right; font-weight: 800; }
.device-block__error { color: #991b1b; font-size: 13px; }
.device-block__actions { display: flex; flex-wrap: wrap; gap: 10px; }
button { min-height: 44px; padding: 10px 16px; border: 1px solid #d5dae3; border-radius: 12px; background: #fff; color: #27344a; font-weight: 800; cursor: pointer; }
button:disabled { opacity: .6; cursor: wait; }
.device-block__primary { border-color: #e8651a; background: #e8651a; color: #fff; }
@media (max-width: 520px) { .device-block__card { padding: 22px; } dl div { display: grid; gap: 3px; } dd { text-align: left; } .device-block__actions button { width: 100%; } }
</style>
